import { Button } from "@/components/ui/button";
import { AutomatizationStep } from "../types/cartAutomatizationStatus"
import { cn } from "@/lib/utils";
import { ExternalLinkIcon } from "lucide-react";
import { getStoreBdData } from "utils/storeDataManager";
import { useContext, useState } from "react";
import { AppDataContext } from "modules/main/context/appData";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Item, ItemContent, ItemDescription, ItemGroup, ItemTitle } from "@/components/ui/item";
import { MainLogo } from "modules/main/assets/main_logo";
import { Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";
import Amount from "utils/amount";
import posthog from "posthog-js";
import { SelectScenarioHandler } from "../types/scenarioSelection";


const PAY_IN_STORE_EVENT_CODE = "Pagar en tienda";


interface FinishProcessButtonProps extends React.HTMLAttributes<HTMLDivElement>{
    status: AutomatizationStep;
    scenarioId: number;
    isFirstTime?: boolean;
    onSelectScenario?: SelectScenarioHandler;
}

export const FinishProcessButton = ({ status, scenarioId, className, isFirstTime, onSelectScenario }: FinishProcessButtonProps) => {
    
    const launchCheckout = (checkout_url:string) => {
        window.open(`https://${checkout_url}`, "_blank");
    }

    return(
        <div className={cn("", className)}>
            <FinishDialog 
                scenarioId={scenarioId} 
                storesAutomatizationStep={status}
                launchCheckout={launchCheckout}
                onSelectScenario={onSelectScenario}
            />
            {

            }
        </div>
    )
}

interface FinishDialogProps extends React.HTMLAttributes<HTMLDivElement>{
    scenarioId: number;
    storesAutomatizationStep: AutomatizationStep;
    launchCheckout: (checkout_url:string) => void;
    onSelectScenario?: SelectScenarioHandler;

}

const FinishDialog = ({scenarioId, storesAutomatizationStep, launchCheckout, onSelectScenario}: FinishDialogProps) => {
    const {storeList} = useContext(AppDataContext); 
    const [isPaying, setIsPaying] = useState(false);

    const storeInfo = getStoreBdData(storesAutomatizationStep.store_id,storeList);
    const store_name = storeInfo?.name ?? "";
    const store_logo = storeInfo?.logo_url ?? "";

    const reportSelectionError = (error: unknown) => {
        if (typeof window !== "undefined" && "errorReporting" in window) {
            (window as Window & {
                errorReporting?: { captureException: (exception: unknown, context?: unknown) => void };
            }).errorReporting?.captureException(error, {
                tags: { scope: "cartCreation", action: "selectScenario" },
                extra: { storeId: storesAutomatizationStep.store_id, scenarioId }
            });
        }
    };

    const onPayInStoreClick = async () => {
        if (isPaying) {
            return;
        }

        setIsPaying(true);
        posthog.capture(PAY_IN_STORE_EVENT_CODE, {
            store_id: storesAutomatizationStep.store_id,
            scenario_id: scenarioId,
        });

        try {
            if (scenarioId > 0 && onSelectScenario) {
                await onSelectScenario(scenarioId);
            }
        } catch (error) {
            reportSelectionError(error);
        } finally {
            launchCheckout(storeInfo?.checkout_url ?? "#");
            setIsPaying(false);
        }
    }

    return(
    <Dialog>
        <DialogTrigger asChild>
            <Button size={"xs"}>
                <ExternalLinkIcon className="w-4 h-4 mr-1"/>
                Pagar <Amount 
                        amount={storesAutomatizationStep.results?.totalPriceInCart ?? 0}
                        currency="CLP" 
                        locale="es-CL"  
                    />                
            </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-sm">
            <MainLogo classname="h-14 mx-auto"/>
            <DialogHeader>
            <DialogTitle className="text-center text-lg">¡Tu carrito en {store_name} está listo!</DialogTitle>
            <DialogDescription className="text-sm">
                <p className="text-center">Te llevaremos al sitio de {store_name} para pagar seguro (<span className="font-semibold text-foreground">Puede que tengas que volver a iniciar sesión</span>). Mientras tanto, seguiremos armando el resto de tus carritos.</p>                
            </DialogDescription>
            </DialogHeader>
                <ItemGroup>
                    <Item variant={"outline"}>
                        <ItemContent className="gap-2">                                        
                            <ItemTitle className="flex flex-col gap-0 w-full items-start">
                                <div className="flex flex-row w-full justify-between">
                                    <div className="flex flex-row items-center gap-2">
                                        <Avatar className="size-7">
                                            <AvatarImage src={store_logo}/>
                                            <AvatarFallback>{store_name.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                        <span className="text-muted-foreground">{store_name}</span>
                                    </div>
                                    <span>
                                    { storesAutomatizationStep.status === "PARTIAL" && (
                                        <span>*</span>
                                    )}
                                    <Amount 
                                        amount={storesAutomatizationStep.results?.totalPriceInCart ?? 0}
                                        className="font-semibold"
                                        currency="CLP" 
                                        locale="es-CL"  
                                    />
                                    </span>
                                </div>
                                
                                { storesAutomatizationStep.status === "PARTIAL" && (
                                    <span className="text-muted-foreground text-xxs mx-auto">* No se están considerando los productos sin stock</span>
                                )}
                            </ItemTitle>
                            <ItemDescription className="w-full">
                                <Button className="w-full" size={"lg"} onClick={onPayInStoreClick} disabled={isPaying}>
                                    <span>{isPaying ? "Abriendo tienda..." : `Pagar en ${store_name}`}</span>
                                    <ExternalLinkIcon className="w-4 h-4 ml-2"/>
                                </Button>
                            </ItemDescription>
                        </ItemContent>                                 
                    </Item>                                                
                </ItemGroup>      
            <DialogFooter>
                <p className="text-center text-xs text-muted-foreground">Una vez que pagues en {store_name}, vuelve aqui para continuar con las demás tiendas.</p>
            </DialogFooter>                      
        </DialogContent>
    </Dialog>
    );
}
